/**
 * Image mirroring — every photo a user picks gets copied into our own
 * user-images bucket before its URL ever lands on a card row. Cards then
 * reference a Supabase Storage URL we fully control, which removes our
 * exposure to upstream CDN flakes, rate limits, temporary URLs (Pixabay's
 * expire within a day), and individual photo takedowns.
 *
 * Owner ruling 2026-09-05 (docs/DECISIONS.md): a save that still points at
 * Pexels / Unsplash / Pixabay / Google is the old bug. We copy, or we fail
 * the save visibly. Falling back to the rented URL is forbidden — those
 * links rot, and six months of this project proved it. The nightly
 * self-heal sweep is only for cards that already slipped through, not a
 * license to keep writing fragile rows.
 *
 * Lives in its own module (not api.ts) because dataApi.ts — the low-level
 * CRUD layer — enforces mirroring on every card write and must not depend
 * on the high-level edge-function caller module.
 */

import { supabase } from '@/lib/supabase';
import { reportError } from '@/services/errorTracking';
import { withRetry } from './requestUtils';

/** Thrown when a third-party photo could not be copied into our storage. */
export class MirrorFailedError extends Error {
  readonly sourceUrl: string;
  constructor(sourceUrl: string, cause?: unknown) {
    super("Couldn't copy the photo into Vidi storage");
    this.name = 'MirrorFailedError';
    this.sourceUrl = sourceUrl;
    if (cause !== undefined) {
      (this as Error & { cause?: unknown }).cause = cause;
    }
  }
}

function ownStorageHost(): string | null {
  try {
    return new URL(import.meta.env.VITE_SUPABASE_URL).hostname;
  } catch {
    return null;
  }
}

/**
 * Mirror ANY external https image URL. Only three shapes are exempt:
 * our own Supabase project (already stable storage — includes AI-generated
 * images and user uploads), data:/blob: URIs (no host to rot), and
 * non-absolute strings (nothing to fetch). The server enforces its own
 * validation (SSRF hardening for unknown hosts) — this predicate only
 * decides whether a round-trip is worth making.
 */
export function shouldMirror(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    const ownHost = ownStorageHost();
    return ownHost !== null && parsed.hostname !== ownHost;
  } catch {
    return false; // relative path, data:/blob: URI, or garbage — nothing to mirror
  }
}

/**
 * What may land on cards.image_url: an empty draft (batch / word-pack Skip —
 * never reviewable) or a URL on our own storage host. Everything else is a
 * rented link and must not be persisted.
 */
export function isDurableCardImageUrl(url: string): boolean {
  if (url === '') return true;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    const ownHost = ownStorageHost();
    return ownHost !== null && parsed.hostname === ownHost;
  } catch {
    return false;
  }
}

/**
 * Retry predicate for mirrorImage: only transient failures.
 * Non-retryable (permanent): 400 (bad URL/SSRF), 404 (photo deleted/definitive)
 * Retryable (transient): network error/TypeError, timeout/abort, 429, 5xx
 * Unknown status (undefined) = network error = retryable
 */
function isMirrorRetryable(err: unknown): boolean {
  if (!navigator.onLine) return true;
  if (err instanceof TypeError) return true;
  const status = (err as { status?: number }).status;
  if (err instanceof Error && /timeout|abort|fetch/i.test(err.message)) return true;
  if (status === 400 || status === 404) return false;
  if (status && (status === 429 || status >= 500)) return true;
  return status === undefined;
}

/**
 * Mirror a remote image URL to Supabase Storage and return the persistent
 * Supabase URL. If `url` is not mirror-eligible (already ours, data: URI),
 * it is returned unchanged — callers never need to branch.
 *
 * On failure after retries the call throws MirrorFailedError. The create /
 * replace / swap flows catch that and fail the save visibly so the learner
 * can retry or pick another photo. We do not persist the rented URL.
 */
export async function mirrorImage(url: string): Promise<string> {
  if (!shouldMirror(url)) return url;
  try {
    return await withRetry(
      async () => {
        const { data, error } = await supabase.functions.invoke('mirror-image', { body: { url } });
        if (error) {
          throw Object.assign(new Error(error.message), {
            status: (error as { status?: number }).status,
          });
        }
        const mirroredUrl = (data as { imageUrl?: string } | null)?.imageUrl;
        // A 200 that still hands back a Pexels/Pixabay URL is the old silent
        // save. Treat it as a failed copy, not a success.
        if (typeof mirroredUrl !== 'string' || !mirroredUrl || shouldMirror(mirroredUrl)) {
          throw Object.assign(new Error('mirror-image did not return our storage URL'), {
            status: 500,
          });
        }
        return mirroredUrl;
      },
      { maxRetries: 2, baseDelayMs: 800, shouldRetry: isMirrorRetryable },
    );
  } catch (err) {
    if (err instanceof MirrorFailedError) throw err;
    reportError(err, { operation: 'mirrorImage', extra: { sourceUrl: url } });
    throw new MirrorFailedError(url, err);
  }
}
